import React from 'react';
import { useDispatch } from 'react-redux';
import { showSuccessBanner } from '@utils/application';
import { getSpec, createSpec, replaceSpec } from '@services/scoreSpecs';
import { setAt, unsetAt } from './setAt';
import { toPayload } from './payload';

const CREATE_ID = 'new';

// A fresh authored draft. enabled_in / backfill_only were removed from the format and are
// intentionally omitted from newly authored specs; `toPayload` strips them from legacy ones.
export const blankDefinition = () => ({
  score_spec_id: '',
  version: 1,
  description: '',
  notes: '',
  trigger: {},
  document_set: {},
  score_gen: {},
  output_extraction: {},
});

// Derives a create seed for "Duplicate as next version": bumps the trailing _vN in the id and the
// version field, keeping the rest of the definition. Feeds SpecEditor via router location state.
export const nextVersionSeed = (definition = {}) => {
  const id = String(definition.score_spec_id || '');
  const match = /_v(\d+)$/i.exec(id);
  const currentVersion = match ? Number(match[1]) : Number(definition.version) || 1;
  const nextVersion = currentVersion + 1;
  const base = id.replace(/_v\d+$/i, '');
  return { ...definition, score_spec_id: base ? `${base}_v${nextVersion}` : '', version: nextVersion };
};

// Owns the draft editor buffer: loads the record (also the concurrency refetch), holds the
// full definition being edited + a revert snapshot, and persists via create/replace. Clones the
// load -> edit -> revert/save shape of AgentScriptEditor. Save stays in edit mode unless 200.
export const useSpecEditor = (specId, seed) => {
  const dispatch = useDispatch();
  const isCreate = specId === CREATE_ID;

  const [spec, setSpec] = React.useState(null);
  const [definition, setDefinition] = React.useState(() => (isCreate ? seed || blankDefinition() : null));
  const [revert, setRevert] = React.useState(() => (isCreate ? seed || blankDefinition() : null));
  const [error, setError] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(!isCreate);
  // Bumped whenever the buffer is replaced wholesale from OUTSIDE the form — a revert, a reload,
  // or a save round-trip. The raw JSON panes watch it so they drop held text that a value
  // comparison cannot detect as stale (reverting to the value the section already had).
  const [bufferEpoch, setBufferEpoch] = React.useState(0);
  // Bumped ONLY when the STORED row is replaced — a load or a save. A revert replaces the buffer
  // but not the row on the server, so anything keyed to what the server holds (a preview run, which
  // compiled that row) must not be thrown away by one. bufferEpoch cannot carry this: it bumps on
  // reverts too, by design, because the raw panes need to drop held text on any wholesale change.
  const [savedEpoch, setSavedEpoch] = React.useState(0);

  const load = React.useCallback(async () => {
    if (isCreate) return;
    setIsLoading(true);
    setError(null);
    const { spec: loaded, error: loadError } = await getSpec(specId);
    if (loaded) {
      setSpec(loaded);
      setDefinition(loaded.spec_json);
      setRevert(loaded.spec_json);
      setBufferEpoch((e) => e + 1);
      setSavedEpoch((e) => e + 1);
    } else {
      setError(loadError);
    }
    setIsLoading(false);
  }, [specId, isCreate]);

  React.useEffect(() => {
    load();
  }, [load]);

  const isDraft = isCreate || spec?.status === 'draft';
  const isDirty = JSON.stringify(definition) !== JSON.stringify(revert);

  const save = React.useCallback(async () => {
    setIsSaving(true);
    setError(null);
    // The BUFFER is sent through toPayload, never raw: a legacy row still carries keys the format
    // has dropped, and echoing them back on every save keeps them alive in the store.
    const payload = toPayload(definition);
    const res = isCreate ? await createSpec(payload) : await replaceSpec(specId, payload);
    setIsSaving(false);

    if (res.status === 200 && res.spec) {
      setSpec(res.spec);
      setDefinition(res.spec.spec_json);
      setRevert(res.spec.spec_json);
      setBufferEpoch((e) => e + 1);
      setSavedEpoch((e) => e + 1);
      showSuccessBanner(isCreate ? 'Draft created' : 'Draft saved', null, dispatch);
      return res.spec.score_spec_id;
    }
    setError(res.error); // 409 -> refresh prompt; 422 -> inline, stay in edit mode
    return null;
  }, [isCreate, specId, definition, dispatch]);

  const revertEdits = React.useCallback(() => {
    setDefinition(revert);
    setBufferEpoch((e) => e + 1);
  }, [revert]);

  // Path-addressed writes. EVERY form control in the feature goes through these rather than
  // rebuilding the definition itself, because `isDirty` above is a key-order-sensitive
  // JSON.stringify comparison: `setAt` updates an existing key in place and only appends
  // genuinely new ones, so opening a spec and touching one field cannot flip isDirty on a
  // buffer that is semantically identical.
  //
  // Note there is deliberately no "commit pending raw editors" step before save: the raw `{ }`
  // panes write on every keystroke that parses, so there is never a valid edit sitting
  // uncommitted for a save to race. Text that does NOT parse is held locally and never written,
  // which is the behaviour we want — a half-typed brace must not reach the buffer.
  const setValue = React.useCallback((path, value) => setDefinition((d) => setAt(d, path, value)), []);
  const removeValue = React.useCallback((path) => setDefinition((d) => unsetAt(d, path)), []);
  const replaceSection = React.useCallback((key, value) => setDefinition((d) => setAt(d, key, value)), []);

  // A section revert is also an outside replacement, so it stamps too.
  const revertSection = React.useCallback(
    (key) => {
      setDefinition((d) => setAt(d, key, (revert || {})[key] === undefined ? {} : revert[key]));
      setBufferEpoch((e) => e + 1);
    },
    [revert]
  );

  // The definition this draft was duplicated FROM, captured once at mount. Drives the
  // "Changed from <id> · N paths" strip. Null when authoring from scratch or editing in place —
  // there is nothing to have changed from.
  const [seedOrigin] = React.useState(() => (isCreate && seed ? seed : null));

  return {
    spec,
    isDraft,
    isLoading,
    definition,
    setDefinition,
    setValue,
    removeValue,
    replaceSection,
    seedOrigin,
    savedDefinition: revert,
    revertSection,
    bufferEpoch,
    savedEpoch,
    isDirty,
    isSaving,
    error,
    save,
    revertEdits,
    reload: load,
  };
};
